import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { useOwnerQa } from "../contexts/OwnerQaContext";
import { puzzleTrainingRequest } from "../training-payments/dailyPuzzleApi";
import "./PuzzleTrainingQaPanel.css";

/**
 * Owner-only web-puzzle simulator. Real Stripe, scores and daily quota
 * remain untouched. All operations are owner-verified again by the API.
 */
export default function PuzzleTrainingQaPanel() {
  const qa = useOwnerQa();
  const { firebaseUser } = useContext(UserContext);
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mode = qa.active ? qa.config.puzzleTrainingMode : "real";

  useEffect(() => {
    if (!qa.isOwner || !qa.active || !firebaseUser) {
      setAccess(null);
      return undefined;
    }
    let cancelled = false;
    setError("");
    puzzleTrainingRequest("access", {
      params: mode === "real" ? undefined : { qaMode: mode },
    }).then((data) => {
      if (!cancelled) setAccess(data);
    }).catch(() => {
      if (!cancelled) setError("Could not load the training QA status. Try again.");
    });
    return () => { cancelled = true; };
  }, [qa.active, qa.isOwner, firebaseUser?.uid, mode]);

  if (!qa.isOwner) return null;

  const setMode = (value) => {
    if (!qa.active) qa.setEnabled(true);
    qa.setPuzzleTrainingMode(value);
    setAccess(null);
    setError("");
  };

  const applyAction = async (action) => {
    if (!qa.active || !firebaseUser || busy) return;
    setBusy(true);
    setError("");
    try {
      const next = await puzzleTrainingRequest("qa", { body: { action } });
      if (mode !== "free") qa.setPuzzleTrainingMode("free");
      setAccess(next);
    } catch (err) {
      setError("Could not change the QA allowance: " + (err?.message || "Please retry."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ch-owner-puzzle-qa" aria-label="Owner puzzle subscription testing">
      <h3>Chesshood Training · Owner QA</h3>
      <p>
        Test the £0.90/month puzzle membership as a new user, without changing
        your real Chesshood Pro subscription or charging your card.
      </p>
      <div className="qa-controls">
        <label className="qa-switch">
          <input type="checkbox" checked={qa.active}
            onChange={(event) => qa.setEnabled(event.target.checked)} />
          Enable owner testing
        </label>
        <label htmlFor="owner-puzzle-qa-mode" className="qa-mode-label">
          Simulated puzzle subscription
        </label>
        <select id="owner-puzzle-qa-mode" className="qa-option"
          value={qa.config.puzzleTrainingMode}
          onChange={(event) => setMode(event.target.value)}>
          <option value="real">Real account (normal access)</option>
          <option value="free">FREE – 7 daily puzzles, then paywall</option>
          <option value="pro">SUBSCRIBED – unlimited puzzles (£0.90 simulation)</option>
        </select>
        <div className="qa-stat" aria-live="polite">
          {qa.active && mode !== "real"
            ? <>Simulated status: <strong>{mode === "free" ? "FREE" : "SUBSCRIBED"}</strong>
              {" · "}{access
                ? (access.usedToday + "/7 puzzles used · " + access.remainingToday + " left")
                : "Loading counter…"}
            </>
            : <>Status: <strong>{qa.active ? "REAL ACCOUNT" : "QA OFF"}</strong></>}
        </div>
      </div>
      <div className="qa-actions">
        <button type="button" disabled={busy || !qa.active} onClick={() => applyAction("reset")}>
          Reset to 0/7
        </button>
        <button type="button" className="qa-secondary" disabled={busy || !qa.active}
          onClick={() => applyAction("exhaust")}>
          Simulate 7/7 used
        </button>
        <Link to="/training?dailyLimit=1">Preview paywall</Link>
        <Link to="/play">Open training board</Link>
      </div>
      {error && <p className="qa-error" role="alert">{error}</p>}
      <p className="qa-explainer">
        Only the verified owner can use these controls. QA progress is separate
        from real Stripe billing, real ratings and genuine daily credits. Reset
        whenever you want to repeat the seven-puzzle test.
      </p>
    </section>
  );
}
